import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { apiService } from '../../services/api';

const GREEN = '#2e7d32';
const RED = '#c62828';

type ProfitPeriod = 'last365' | 'ytd' | 'quarter' | 'month' | 'last90';
type Flag = 'low_rate' | 'limited_time_data' | 'no_revenue';

interface PropertyProfitRow {
  propertyKey: string;
  customerName: string;
  address: string;
  services: string[];
  visits: number;
  timedVisits: number;
  revenue: number;
  platformFees: number;
  avgRevenuePerVisit: number | null;
  avgMinutesOnSite: number | null;
  avgEstimatedMinutes: number | null;
  crewHours: number;
  revenuePerCrewHour: number | null;
  laborCost: number | null;
  laborCostComplete: boolean;
  margin: number | null;
  marginPercent: number | null;
  hasSeasonalContract: boolean;
  seasonalValueEstimated: boolean;
  flags: Flag[];
  upcomingPerVisitRevenue90: number;
  upcomingVisits90: number;
}

export interface PropertyProfitData {
  period: ProfitPeriod;
  rows: PropertyProfitRow[];
  summary: {
    properties: number;
    revenue: number;
    crewHours: number;
    revenuePerCrewHour: number | null;
    medianRevenuePerCrewHour: number | null;
    threshold: number | null;
    thresholdSource: 'target' | 'median' | null;
    targetRevenuePerCrewHour: number | null;
    flaggedCount: number;
    visits: number;
    untimedVisits: number;
    hasPayRates: boolean;
    laborCost: number | null;
  };
}

const PERIOD_LABELS: Record<ProfitPeriod, string> = {
  last365: 'Last 12 months',
  ytd: 'Year to date',
  quarter: 'This quarter',
  month: 'This month',
  last90: 'Last 90 days',
};

const money = (n: number | null | undefined) =>
  n == null
    ? '—'
    : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const wholeMoney = (n: number | null | undefined) =>
  n == null ? '—' : `$${Math.round(Number(n)).toLocaleString()}`;

const StatCard: React.FC<{ label: string; value: string; sub?: string; accent?: string }> = ({
  label,
  value,
  sub,
  accent = GREEN,
}) => (
  <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', height: '100%' }}>
    <CardContent>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="h5" sx={{ fontWeight: 700, color: accent }}>{value}</Typography>
      {sub && (
        <Typography variant="caption" color="text.secondary">{sub}</Typography>
      )}
    </CardContent>
  </Card>
);

const PropertyProfitTab: React.FC = () => {
  const [period, setPeriod] = useState<ProfitPeriod>('last365');
  const [data, setData] = useState<PropertyProfitData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [onlyFlagged, setOnlyFlagged] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [increasePercent, setIncreasePercent] = useState('10');
  const [targetInput, setTargetInput] = useState('');
  const [savingTarget, setSavingTarget] = useState(false);
  const [targetMessage, setTargetMessage] = useState<string | null>(null);

  const load = useCallback(async (p: ProfitPeriod) => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiService.get<PropertyProfitData>('/provider-financial/property-profit', { period: p });
      if (res.success && res.data) {
        setData(res.data);
        setTargetInput(res.data.summary.targetRevenuePerCrewHour ? String(res.data.summary.targetRevenuePerCrewHour) : '');
      } else {
        throw new Error(res.error || 'Failed to load property profit');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load property profit');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(period);
  }, [period, load]);

  const saveTarget = async (value: number | null) => {
    try {
      setSavingTarget(true);
      setTargetMessage(null);
      const res = await apiService.put<{ targetRevenuePerCrewHour: number | null }>(
        '/provider-financial/property-profit/target',
        { targetRevenuePerCrewHour: value }
      );
      if (!res.success) throw new Error(res.error || 'Failed to save target');
      setTargetMessage(value == null ? 'Using your automatic comparison again.' : `Target saved: ${money(value)} per crew-hour.`);
      await load(period);
    } catch (err: any) {
      setTargetMessage(err.message || 'Failed to save target');
    } finally {
      setSavingTarget(false);
    }
  };

  const rows = useMemo(
    () => (data ? (onlyFlagged ? data.rows.filter((r) => r.flags.includes('low_rate')) : data.rows) : []),
    [data, onlyFlagged]
  );

  const calculator = useMemo(() => {
    if (!data) return null;
    const pct = Number(increasePercent);
    if (!Number.isFinite(pct) || pct <= 0) return null;
    const chosen = data.rows.filter((r) => selected.has(r.propertyKey));
    if (chosen.length === 0) return null;
    const upcomingRevenue = chosen.reduce((s, r) => s + r.upcomingPerVisitRevenue90, 0);
    const upcomingVisits = chosen.reduce((s, r) => s + r.upcomingVisits90, 0);
    const rated = chosen.filter((r) => r.revenuePerCrewHour != null && !r.hasSeasonalContract);
    const ratedRevenue = rated.reduce((s, r) => s + r.revenue * (r.visits > 0 ? r.timedVisits / r.visits : 0), 0);
    const ratedHours = rated.reduce((s, r) => s + r.crewHours, 0);
    const currentRate = ratedHours > 0 ? ratedRevenue / ratedHours : null;
    return {
      count: chosen.length,
      extraRevenue: upcomingRevenue * (pct / 100),
      upcomingVisits,
      currentRate,
      newRate: currentRate != null ? currentRate * (1 + pct / 100) : null,
      seasonalCount: chosen.filter((r) => r.hasSeasonalContract).length,
    };
  }, [data, selected, increasePercent]);

  const toggle = (key: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  if (loading && !data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return null;

  const s = data.summary;
  const thresholdText =
    s.thresholdSource === 'target'
      ? `Flagging properties below your target of ${money(s.threshold)} per crew-hour.`
      : s.thresholdSource === 'median'
        ? `Flagging properties below ${money(s.threshold)} per crew-hour — 75% of your typical property (${money(s.medianRevenuePerCrewHour)}).`
        : 'Not enough timed visits yet to compare properties automatically (needs 3 properties with 2+ crew-app visits). You can set a target instead.';

  return (
    <Stack spacing={3}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 640 }}>
          What each property earns for the time your crews spend there. Time comes from the crew app&apos;s Arrived and Complete taps.
        </Typography>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Period</InputLabel>
          <Select label="Period" value={period} onChange={(e) => setPeriod(e.target.value as ProfitPeriod)}>
            {(Object.keys(PERIOD_LABELS) as ProfitPeriod[]).map((p) => (
              <MenuItem key={p} value={p}>{PERIOD_LABELS[p]}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={6} md={3}>
          <StatCard label="Revenue" value={money(s.revenue)} sub={`${s.properties} properties`} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="Crew-hours on site" value={s.crewHours.toLocaleString()} sub={`${s.visits - s.untimedVisits} of ${s.visits} visits timed`} accent="#37474f" />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="Revenue per crew-hour" value={money(s.revenuePerCrewHour)} sub="Across timed visits" />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label="Possibly underpriced"
            value={String(s.flaggedCount)}
            sub={s.flaggedCount === 1 ? 'property' : 'properties'}
            accent={s.flaggedCount > 0 ? RED : GREEN}
          />
        </Grid>
      </Grid>

      {s.untimedVisits > 0 && (
        <Alert severity="info">
          {s.untimedVisits} of {s.visits} visits were marked complete from the dashboard, without crew check-in and check-out, so they have no time on site. Those visits aren&apos;t used for time-based numbers. Have crews use Arrived and Complete in the crew app to track time.
        </Alert>
      )}
      {!s.hasPayRates && (
        <Alert severity="info">
          Add pay rates for your employees in{' '}
          <Button component={RouterLink} to="/provider/crew-management" size="small" sx={{ textTransform: 'none', p: 0, minWidth: 0, verticalAlign: 'baseline' }}>
            Crew Management
          </Button>{' '}
          to see labor cost and margin per property.
        </Alert>
      )}

      <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
        <Typography fontWeight={600} sx={{ mb: 0.5 }}>Underpriced flag</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{thresholdText}</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
          <TextField
            size="small"
            label="Target per crew-hour"
            type="number"
            value={targetInput}
            onChange={(e) => setTargetInput(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            inputProps={{ min: 1, step: 1 }}
            sx={{ maxWidth: 220 }}
          />
          <Button
            variant="contained"
            disabled={savingTarget || !(Number(targetInput) > 0)}
            onClick={() => saveTarget(Number(targetInput))}
            sx={{ bgcolor: GREEN, '&:hover': { bgcolor: '#1b5e20' } }}
          >
            Save target
          </Button>
          {s.targetRevenuePerCrewHour != null && (
            <Button disabled={savingTarget} onClick={() => saveTarget(null)}>
              Use automatic comparison
            </Button>
          )}
        </Stack>
        {targetMessage && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>{targetMessage}</Typography>
        )}
      </Paper>

      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Typography fontWeight={600}>Properties</Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            {s.flaggedCount > 0 && (
              <Button
                size="small"
                onClick={() => setSelected(new Set(data.rows.filter((r) => r.flags.includes('low_rate')).map((r) => r.propertyKey)))}
              >
                Select flagged
              </Button>
            )}
            {selected.size > 0 && (
              <Button size="small" onClick={() => setSelected(new Set())}>Clear selection</Button>
            )}
            <FormControlLabel
              control={<Switch size="small" checked={onlyFlagged} onChange={(e) => setOnlyFlagged(e.target.checked)} />}
              label={<Typography variant="body2">Only flagged</Typography>}
            />
          </Stack>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>Property</TableCell>
                <TableCell align="right">Visits</TableCell>
                <TableCell align="right">Avg / visit</TableCell>
                <TableCell align="right">Avg time on site</TableCell>
                <TableCell align="right">Crew-hours</TableCell>
                <TableCell align="right">$ / crew-hour</TableCell>
                <TableCell align="right">Labor cost</TableCell>
                <TableCell align="right">Margin</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {onlyFlagged ? 'No properties are flagged.' : 'No completed visits in this period.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((r) => {
                  const low = r.flags.includes('low_rate');
                  return (
                    <TableRow key={r.propertyKey} hover selected={selected.has(r.propertyKey)}>
                      <TableCell padding="checkbox">
                        <Checkbox size="small" checked={selected.has(r.propertyKey)} onChange={() => toggle(r.propertyKey)} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>{r.customerName}</Typography>
                        <Typography variant="caption" color="text.secondary" display="block">{r.address}</Typography>
                        <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap' }} useFlexGap>
                          {r.services.map((svc) => (
                            <Chip key={svc} size="small" variant="outlined" label={svc} />
                          ))}
                          {r.seasonalValueEstimated && (
                            <Tooltip title="Seasonal contract: each visit is credited the contract price divided by the visits expected in the season.">
                              <Chip size="small" label="Seasonal contract" />
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell align="right">{r.visits}</TableCell>
                      <TableCell align="right">{money(r.avgRevenuePerVisit)}</TableCell>
                      <TableCell align="right">
                        {r.avgMinutesOnSite != null ? `${r.avgMinutesOnSite} min` : '—'}
                        {r.avgEstimatedMinutes != null && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            est. {r.avgEstimatedMinutes} min
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">{r.timedVisits > 0 ? r.crewHours : '—'}</TableCell>
                      <TableCell align="right">
                        {r.revenuePerCrewHour == null ? (
                          <Tooltip title="Needs crew-app visits with check-in and check-out times.">
                            <Typography variant="body2" color="text.secondary">No time data</Typography>
                          </Tooltip>
                        ) : low ? (
                          <Chip size="small" label={wholeMoney(r.revenuePerCrewHour)} sx={{ bgcolor: '#ffebee', color: RED, fontWeight: 600 }} />
                        ) : (
                          <Typography variant="body2" fontWeight={500}>{wholeMoney(r.revenuePerCrewHour)}</Typography>
                        )}
                        {r.revenuePerCrewHour != null && r.flags.includes('limited_time_data') && (
                          <Typography variant="caption" color="text.secondary" display="block">1 timed visit</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {r.laborCost == null ? '—' : money(r.laborCost)}
                        {r.laborCost != null && !r.laborCostComplete && (
                          <Typography variant="caption" color="text.secondary" display="block">partial</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {r.margin == null ? (
                          '—'
                        ) : (
                          <Typography variant="body2" sx={{ color: r.margin >= 0 ? GREEN : RED, fontWeight: 500 }}>
                            {money(r.margin)}
                            {r.marginPercent != null && (
                              <Typography component="span" variant="caption" color="text.secondary"> ({r.marginPercent}%)</Typography>
                            )}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
        <Typography fontWeight={600} sx={{ mb: 0.5 }}>Price increase calculator</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Check properties in the table, then enter a percentage to see what raising their per-visit prices would add over the next 90 days.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <TextField
            size="small"
            label="Increase"
            type="number"
            value={increasePercent}
            onChange={(e) => setIncreasePercent(e.target.value)}
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
            inputProps={{ min: 1, max: 100, step: 1 }}
            sx={{ maxWidth: 140 }}
          />
          {calculator ? (
            <Box>
              <Typography variant="body1">
                <strong>+{money(calculator.extraRevenue)}</strong> over the next 90 days across {calculator.upcomingVisits} scheduled visit{calculator.upcomingVisits === 1 ? '' : 's'} at {calculator.count} propert{calculator.count === 1 ? 'y' : 'ies'}.
              </Typography>
              {calculator.currentRate != null && calculator.newRate != null && (
                <Typography variant="body2" color="text.secondary">
                  Revenue per crew-hour: {wholeMoney(calculator.currentRate)} → {wholeMoney(calculator.newRate)}
                </Typography>
              )}
              {calculator.seasonalCount > 0 && (
                <Typography variant="caption" color="text.secondary" display="block">
                  Seasonal contract prices are fixed for the season; change them when you send the renewal offer.
                </Typography>
              )}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">Select at least one property.</Typography>
          )}
        </Stack>
      </Paper>

      <Typography variant="caption" color="text.secondary">
        Revenue is the service price before sales tax. Crew-hours are time on site times crew size (or each employee&apos;s minutes when crews log who worked). Drive time between stops isn&apos;t included.
      </Typography>
    </Stack>
  );
};

export default PropertyProfitTab;
